/**
 * UX1: Navigation Taxonomy Types
 *
 * Implements Miller's Law (7±2 items for working memory) by organizing
 * navigation into 5 semantic categories instead of 13+ flat items.
 *
 * Categories:
 * 1. About (👤) - Personal background and introduction
 * 2. Work (💼) - Professional work and evaluations
 * 3. Content (📝) - Articles, insights, and thought leadership
 * 4. Platform (⚙️) - Power reversal features
 * 5. Admin (🔒) - Administrative functions (auth-gated)
 */

export interface NavLink {
  path: string
  label: string
  description?: string
  requiredFeature?: string
  requiredRole?: 'admin' | 'editor' | 'viewer'
}

export interface NavCategory {
  id: string
  label: string
  icon: string
  description: string
  links: NavLink[]
  isCollapsible: boolean
}

/**
 * Navigation taxonomy following UX1 plan
 * 5 categories with clear semantic groupings
 */
export const NAV_CATEGORIES: NavCategory[] = [
  {
    id: 'about',
    label: 'About',
    icon: '👤',
    description: 'Personal background and interests',
    isCollapsible: true,
    links: [
      {
        path: '/',
        label: 'Home',
        description: 'Introduction and overview',
      },
      {
        path: '/background',
        label: 'Background',
        description: 'Professional history and experience',
      },
      {
        path: '/interests',
        label: 'Interests',
        description: 'Personal interests and passions',
      },
    ],
  },
  {
    id: 'work',
    label: 'Work',
    icon: '💼',
    description: 'Professional projects and evaluations',
    isCollapsible: true,
    links: [
      {
        path: '/projects',
        label: 'Projects',
        description: 'Portfolio of work and contributions',
      },
      {
        path: '/organizations',
        label: 'Organizations',
        description: 'Companies with transparency scores',
      },
      {
        path: '/roles',
        label: 'Roles',
        description: 'Open positions with salary transparency',
      },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    icon: '📝',
    description: 'Articles, insights, and thought leadership',
    isCollapsible: true,
    links: [
      {
        path: '/writings',
        label: 'Writings',
        description: 'Blog posts and articles',
      },
      {
        path: '/portfolio-intelligence',
        label: 'Portfolio Intelligence',
        description: 'AI-powered insights and analytics',
      },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    icon: '⚙️',
    description: 'Platform features and principles',
    isCollapsible: true,
    links: [
      {
        path: '/cv',
        label: 'Secure CV',
        description: 'Gated CV with multi-perspective views',
      },
      // Future: /principles, /power-reversal-about
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: '🔒',
    description: 'Administrative functions',
    isCollapsible: true,
    links: [
      {
        path: '/admin',
        label: 'Dashboard',
        description: 'Admin dashboard overview',
        requiredRole: 'admin',
      },
      {
        path: '/admin/projects',
        label: 'Projects',
        description: 'Manage projects',
        requiredRole: 'admin',
      },
      {
        path: '/admin/organizations',
        label: 'Organizations',
        description: 'Manage organizations',
        requiredRole: 'admin',
      },
      {
        path: '/admin/roles',
        label: 'Roles',
        description: 'Manage roles',
        requiredRole: 'admin',
      },
      {
        path: '/admin/evaluations',
        label: 'Evaluations',
        description: 'Manage evaluations',
        requiredRole: 'admin',
      },
      {
        path: '/admin/content',
        label: 'Content',
        description: 'Manage content',
        requiredRole: 'admin',
      },
      {
        path: '/admin/theme-controls',
        label: 'Theme Controls',
        description: 'Visual theme configuration',
        requiredRole: 'admin',
      },
    ],
  },
]

/**
 * Get all links from all categories (flat list)
 */
export function getAllNavLinks(): NavLink[] {
  return NAV_CATEGORIES.flatMap((category) => category.links)
}

/**
 * Get visible categories based on user role
 */
export function getVisibleCategories(
  userRole?: 'admin' | 'editor' | 'viewer' | null
): NavCategory[] {
  return NAV_CATEGORIES.map((category) => ({
    ...category,
    links: category.links.filter((link) => {
      // No role requirement = visible to all
      if (!link.requiredRole) return true

      // User not logged in = hide role-gated links
      if (!userRole) return false

      // Check role hierarchy: admin > editor > viewer
      const roleHierarchy = { admin: 3, editor: 2, viewer: 1 }
      const requiredLevel = roleHierarchy[link.requiredRole]
      const userLevel = roleHierarchy[userRole]

      return userLevel >= requiredLevel
    }),
  })).filter((category) => category.links.length > 0) // Hide empty categories
}

/**
 * Find category containing a specific path
 */
export function getCategoryForPath(path: string): NavCategory | undefined {
  return NAV_CATEGORIES.find((category) =>
    category.links.some((link) => link.path === path)
  )
}
